// ── Protocol, entity lifecycle, and message types ──
//
// Combat-specific types (AttackMessage, DamageEvent, ComboState, projectile
// protocol, damage constants, defense mitigation, etc.) were removed in the
// Ritual Unburdening Phase B refactor. This file retains entity lifecycle
// events, faint flow, attendance, home, hearth presence, and restoration
// messages that were co-located here.
/** Protocol version for messages — bump on breaking changes */
export const COMBAT_PROTOCOL_VERSION = 1;
export const MSG_ENTITY_SPAWN = 'entity_spawn';
export const MSG_ENTITY_DESPAWN = 'entity_despawn';
// ── Enemy windup telegraph (kept for Phase C — becomes emotional tells) ──
export const MSG_ENEMY_WINDUP = 'enemy_windup';
// ── B-017 — fainting flow (replaces v1 player-death/respawn) ──
//
// GDD §6.7 + §11.1: HP=0 does NOT trigger respawn. The player faints, the
// camera fades, the server transitions attendance to Ghost, the avatar is
// teleported to the nearest hearth, and they wake at 20% max-HP for 24h
// (the existing `returningWeakenedUntil` window from round-2 H).
export const MSG_PLAYER_FAINT = 'player_faint';
// ── Round 17 Phase B — Player attack (combat in Expedition interiors) ──
//
// GDD §11: combat is atmosphere-driven. The player presses J (light) or K
// (heavy) to swing at the nearest creature. The server resolves the hit,
// applies damage, and provokes the creature (Patrol → Chase). NO damage
// numbers are sent to the client. NO health bars on creatures. The player
// "feels" the outcome through creature flinch, faint, and unburdening.
export const MSG_PLAYER_ATTACK = 'player_attack';
/** Server → Client: a player's attack connected with a creature. Broadcast
 *  to nearby clients so they can render the swing animation + creature
 *  flinch. NO damage numbers — just the hit event. */
export const MSG_PLAYER_ATTACK_HIT = 'player_attack_hit';
/** Player attack balance constants (GDD §11.2). */
export const PLAYER_LIGHT_DAMAGE = 2;
export const PLAYER_HEAVY_DAMAGE = 4;
/** Melee attack range (px). */
export const PLAYER_MELEE_RANGE = 64;
/** Cooldown between attacks (ms). */
export const PLAYER_LIGHT_COOLDOWN_MS = 1500;
export const PLAYER_HEAVY_COOLDOWN_MS = 2500;
// ── Attendance (v2 Softening) client→server requests ──
//
// Canonical design: docs/design/home-and-attendance.md §1.
// These three messages are the full v2 lie/stand verb set. The server
// resolves them into `AttendanceLedger` calls and projects the resulting
// `AttendanceRecord` into `PlayerEntity` schema fields. Structure metadata
// (hearth/shrine id + park position) is carried on the request because the
// ledger's `parkAtHearth` / `parkAtShrine` convenience helpers need it.
//
// Version (`v`) mirrors COMBAT_PROTOCOL_VERSION so a client speaking an
// older protocol is rejected at the room boundary.
export const MSG_ATTENDANCE_LIE_HEARTH = 'attendance_lie_hearth';
export const MSG_ATTENDANCE_LIE_SHRINE = 'attendance_lie_shrine';
export const MSG_ATTENDANCE_STAND = 'attendance_stand';
// ── Hearth Presence Heartbeat (v2 Softening, B-014) ──
//
// Canonical design: docs/design/afk-countermeasures.md §1.
// While a player is in ParkedHearth or ParkedShrine, the server emits a
// `MSG_HEARTH_PRESENCE_PROMPT` every PRESENCE_PROMPT_INTERVAL_MS (with
// jitter) as a gentle "are you still there?" signal. The client renders it
// diegetically — fading particles around the hearth/shrine — never as a
// modal or CAPTCHA. The player acknowledges by sending
// `MSG_HEARTH_PRESENCE_ACK`, which refreshes the diminishing-returns
// curve and resets the prompt timer. Failing to acknowledge before the
// grace period transitions the player to Dreaming (handled server-side).
/** Server → Client: ambient presence prompt fired while parked. */
export const MSG_HEARTH_PRESENCE_PROMPT = 'hearth_presence_prompt';
/** Client → Server: player acknowledged the prompt — refresh the curve. */
export const MSG_HEARTH_PRESENCE_ACK = 'hearth_presence_ack';
// ── Home (v2 Softening) client→server requests ──
//
// Canonical design:
//   - docs/design/home-server-architecture.md §4.5 (message handler surface)
//   - docs/design/home-and-attendance.md §3 (home + hearth semantics)
//   - docs/design/loot-and-materials.md §2 (MaterialCount payload shape)
//
// HomeRoom resolves these into `HomeLedger` calls and broadcasts the
// resulting stage/ledger deltas via schema + server→client events.
// The `v` field mirrors COMBAT_PROTOCOL_VERSION so a client speaking an
// older protocol is rejected at the room boundary.
export const MSG_HOME_CONTRIBUTE = 'home_contribute';
export const MSG_HOME_FINISH_STAGE = 'home_finish_stage';
export const MSG_HOME_DROP_GIFT = 'home_drop_gift';
export const MSG_HOME_COLLECT_GIFTS = 'home_collect_gifts';
export const MSG_HOME_INVITE_ADD = 'home_invite_add';
export const MSG_HOME_INVITE_REMOVE = 'home_invite_remove';
/** Server → Client: ledger updated; new materials credited, maybe a new stage. */
export const MSG_HOME_STAGE_UPDATED = 'home_stage_updated';
/** Server → Client: owner finished + locked the home at the current stage. */
export const MSG_HOME_COMPLETED = 'home_completed';
/** Server → Client: weakening state changed (sagging, pre-regression warning). */
export const MSG_HOME_WEAKENED = 'home_weakened';
/** Server → Client: home regressed — materials above the preserved stage are lost. */
export const MSG_HOME_REGRESSED = 'home_regressed';
/** Server → Client: generic error shape (code + message) for any home action. */
export const MSG_HOME_ERROR = 'home_error';
// ── Pet Egg messages (Round 10 Phase A) ──────────────────────────────
//
// Egg placement + claim flow. The egg appears at the hearth; the owner
// touches it to claim; the 100h bonding clock starts. All state changes
// are server-authoritative; the client sees replicated schema fields +
// these event messages.
/** Client → Server: owner touches their egg to claim it. */
export const MSG_PET_EGG_CLAIM = 'pet_egg_claim';
/** Server → Client: egg was claimed — bonding clock started. */
export const MSG_PET_EGG_CLAIMED = 'pet_egg_claimed';
/** Server → Client: egg status snapshot sent on room join. */
export const MSG_PET_EGG_STATUS = 'pet_egg_status';
// ── Pet entity snapshot (Phase C) ──────────────────────────────────
//
// Sent on OverworldRoom join when the character has a hatched pet.
// The client uses it to set the pet form, personality adjectives, and
// active trait-gated behaviors for the PetSpriteRenderer behavior loop.
/** Server → Client: full pet snapshot for the companion entity. */
export const MSG_PET_SNAPSHOT = 'pet_snapshot';
// ── World restoration (v2 Softening, B-020) ──
//
// Canonical design: GDD §10 (World Restoration & The Dimming Star);
// `docs/design/realms-and-expeditions.md` §7 (per-realm restoration meter).
//
// Server broadcasts whenever a realm's restoration meter changes — the
// client uses this to drive the visible biome shift (B-021, deferred).
// Today the only contributor is `HomeLedger.finishStage`; future
// contributors include pillars, guardians, and expedition completions.
// Payload is intentionally tiny: realm + meter pct. The renderer reads
// `meterPct` and lerps overlay textures between visual tiers (§7.3).
export const MSG_RESTORATION_UPDATED = 'restoration_updated';
//# sourceMappingURL=combat.js.map